"use client";

import { useEffect, useMemo, useState } from "react";

type KnopkaTranslation = {
  title?: string | null;
  description?: string | null;
  locationLabels?: string[] | null;
};

type Params = {
  username?: string | null;
  eventSlug?: string | null;
  eventTypeId?: number | null;
  title?: string | null;
  description?: string | null;
  locale?: string | null;
  interfaceLanguage?: string | null;
  locationLabels?: string[] | null;
};

function normLocale(locale?: string | null): string {
  const raw = (locale || (typeof navigator !== "undefined" ? navigator.language : "en") || "en")
    .split("-")[0]
    ?.toLowerCase();
  return raw || "en";
}

function sameText(a?: string | null, b?: string | null): boolean {
  return (a || "").trim() === (b || "").trim();
}

function sameLabels(a?: string[] | null, b?: string[] | null): boolean {
  const left = (a || []).map((item) => item.trim()).filter(Boolean);
  const right = (b || []).map((item) => item.trim()).filter(Boolean);
  if (left.length !== right.length) return false;
  return left.every((item, index) => item === right[index]);
}

export function useKnopkaEventTranslation({
  username,
  eventSlug,
  eventTypeId,
  title,
  description,
  locale,
  interfaceLanguage,
  locationLabels,
}: Params): KnopkaTranslation | null {
  const targetLocale = normLocale(locale);
  const sourceLocale = normLocale(interfaceLanguage);
  const shouldSkip = Boolean(interfaceLanguage && sourceLocale === targetLocale);
  const normalizedLocationLabels = useMemo(
    () => (locationLabels || []).map((item) => item.trim()).filter(Boolean),
    [locationLabels]
  );

  const requestKey = useMemo(
    () =>
      JSON.stringify({
        username: username || "",
        eventSlug: eventSlug || "",
        eventTypeId: eventTypeId || 0,
        title: title || "",
        description: description || "",
        locationLabels: normalizedLocationLabels,
        locale: targetLocale,
        interfaceLanguage: interfaceLanguage || "",
        shouldSkip,
      }),
    [
      username,
      eventSlug,
      eventTypeId,
      title,
      description,
      normalizedLocationLabels,
      targetLocale,
      interfaceLanguage,
      shouldSkip,
    ]
  );

  const [result, setResult] = useState<KnopkaTranslation | null>(null);

  useEffect(() => {
    if (
      shouldSkip ||
      !username ||
      !eventSlug ||
      (!title && !description && normalizedLocationLabels.length === 0)
    ) {
      setResult(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch("/booking/api/public/event-translation", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username,
            slug: eventSlug,
            event_type_id: eventTypeId || undefined,
            title: title || "",
            description: description || "",
            locale: targetLocale,
            interface_language: interfaceLanguage || undefined,
            location_labels: normalizedLocationLabels.length ? normalizedLocationLabels : undefined,
          }),
          signal: controller.signal,
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!data?.ok || cancelled) return;

        const nextTitle = data.title ?? null;
        const nextDescription = data.description ?? null;
        const nextLocationLabels = Array.isArray(data.location_labels)
          ? data.location_labels.map((item: unknown) => String(item))
          : null;
        if (
          sameText(nextTitle, title) &&
          sameText(nextDescription, description) &&
          sameLabels(normalizedLocationLabels, nextLocationLabels)
        ) {
          setResult(null);
          return;
        }
        setResult({
          title: nextTitle,
          description: nextDescription,
          locationLabels: nextLocationLabels,
        });
      } catch {
        // Keep original title/description on network/translation errors.
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    requestKey,
    shouldSkip,
    username,
    eventSlug,
    eventTypeId,
    title,
    description,
    normalizedLocationLabels,
    targetLocale,
    interfaceLanguage,
  ]);

  return result;
}
